<template>
  <div class="find-your-bill">
    <div class="gradient-panel">

      <sfi-divider>
        <span class="center step-text"> {{ strings.titlePage }}</span>
      </sfi-divider>

      <div class="form sfi-form content-area">
        <div class="grid-x">
          <div class="cell small-12 medium-6 custom-margin">
            <div class="description">
              <p class="show-for-medium"> {{ strings.description }} </p>
              <p class="show-for-small-only description-mobile"> {{ strings.description }} </p>
            </div>
            <div class="show-for-medium" v-if="insuranceIsSfm">
              <span class="note" v-html="strings.note"></span>
            </div>
          </div>
          <div class="cell small-12 medium-6">
            <div class="grid-x">
                <div class="cell small-12 medium-8">
                  <div class="input-policy-number">
                    <label class="bold-title"> {{ strings.policyNumber }} </label>
                    <inline-input-field type="text"
                      :id="'policyNumber'"
                      :name="analyticsConstants.findYourBill.POLICY_NUMBER"
                      v-model="policyNumber"
                      valueFormat="uppercase"
                      :maxCharacters="policyMax"
                      :error="$v.policyNumber.$error || this.validateCredentials"
                      :allowed-characters="/[^0-9a-zA-Z]/g"
                      @blur="$v.policyNumber.$touch()"
                      @change="updateValidateCredentials()">
                      <div slot="errors" id="policyNumberErrorMessages" v-if="$v.policyNumber.$dirty && $v.policyNumber.$invalid || this.validateCredentials === true">
                        <span v-if="!$v.policyNumber.required" class="help-text medium-no-wrap">{{ validationMessages.policyNumber.req }}</span>
                        <span v-else-if="!$v.policyNumber.pattern" class="help-text medium-no-wrap">{{ validationMessages.policyNumber.minLength }}</span>
                      </div>
                    </inline-input-field>
                  </div>
                <span class="help">
                  <tooltip-expanded-trigger expanded-tip-key="whereCanIFindThis">
                    <a :name="analyticsConstants.findYourBill.WHERE_CAN_I_FIND_THIS"> {{ strings.help }} </a>
                  </tooltip-expanded-trigger>
                </span>
              </div>
            </div>
            <div class="grid-x">
              <div class="cell small-12 medium-10">
                <label class="bold-title input-zip-code"> {{ strings.zipCode }} </label>
              </div>
              <div class="cell small-6 medium-5">
                <inline-input-field type="text"
                  id="zipCode"
                  :name="analyticsConstants.findYourBill.ZIP_CODE"
                  v-model="zip"
                  :error="$v.zip.$error || this.validateCredentials"
                  :allowed-characters="/[^0-9]/g"
                  :maxCharacters="zipMax"
                  @blur="$v.zip.$touch()"
                  @change="updateValidateCredentials()">
                  <div slot="errors" id="zipCodeErrorMessages" v-if="$v.zip.$dirty && $v.zip.$invalid || this.validateCredentials === true">
                    <span v-if="!$v.zip.required" class="help-text small-no-wrap">{{ validationMessages.zipCode.req }}</span>
                    <span v-else-if="!$v.zip.pattern" class="help-text small-no-wrap">{{ validationMessages.zipCode.minLength }}</span>
                  </div>
                </inline-input-field>
              </div>
              <div class="cell small-12 medium-12">
                <div class="validate-credentials" slot="errors" v-if="this.validateCredentials === true">
                  <span class="credentials-text-error" id="policyZipComboError"> {{ strings.mismatch }} </span>
                </div>
              </div>
            </div>
            <div class="grid-x">
              <div class="cell small-12 medium-6 flex-container">
                <button v-if="!isTransmittingData"
                  :name="analyticsConstants.findYourBill.FIND_MY_BILL"
                  class="button primary-orange fixed flex-child-auto find-your-bill-next-button"
                  :disabled="$v.$invalid || this.validateCredentials"
                  @click="next">
                  Find My Bill
                </button>
                <div v-else class="find-your-bill-next-button">
                  <loading-element inline></loading-element>
                </div>
              </div>
            </div>
          </div>
          <div class="cell small-12 show-for-small-only note mobile-note" v-if="insuranceIsSfm">
            <span v-html="strings.note"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Utils
import { mapState, mapGetters } from 'vuex';

// Components
import TooltipExpandedTrigger from '@/components/shared/tooltip-expanded-trigger';

// Validation
import { required } from 'vuelidate/lib/validators';
import pattern from './customValidation/pattern';
import validationMessages from '../i18n/en/validation-messages';
import descriptions from '../i18n/en/strings.js';

// Services
import RejectedPoliciesService from '@/services/RejectedPoliciesService';
import AnalyticsService from '@/services/AnalyticsService';

// Constants
import constants from '@/i18n/en/constants';

export default {
  name: 'FindYourBill',
  components: {
    TooltipExpandedTrigger
  },
  created() {
    window.scrollTo(0, 0);
  },
  data() {
    return {
      analyticsConstants: constants.analyticsConstants,
      isTransmittingData: false,
      strings: descriptions.findYourBill,
      policyNumber: this.$store.state.policyNumber,
      zip: this.$store.state.zipCode,
      policyMax: 11,
      zipMax: 5,
      validationMessages: validationMessages.findYourBill,
      validateCredentials: false
    }
  },
  mounted() {
    AnalyticsService.trackPageView(this.analyticsConstants.FIND_YOUR_BILL);
  },
  computed: {
    ...mapState({
      insuranceTypeKey: state => state.insuranceType.key
    }),
    ...mapGetters({
      insuranceIsSfm: 'GET_IS_INSURANCE_SFM'
    })
  },
  methods: {
    async next() {
      if (!this.$v.$invalid) {
        this.isTransmittingData = true;

        // Save the policy number and zip code to the store. These will be needed for all payment mediation API calls.
        // [vw3] Saving these here instead of in the FIND_YOUR_BILL_FORM_SUBMITTED action so that the values are saved
        // in the case of the app routing to an error page for unsupported policies before triggering the action. This
        // will allow us to retrieve the values if we need to route the user to alternate (FL or MS) express pay.
        this.$store.commit('SET_POLICY_IDENTIFIERS', {
          policyNumber: this.policyNumber,
          zipCode: this.zip
        });
        const policyType = this.isPolicySupported(this.policyNumber);
        if (policyType !== 'goToPaymentInfo') {
          this.$store.commit('SET_REDIRECT_TYPE', { redirect: policyType });
          this.goToPage('redirect');
        } else {
          this.$store.dispatch('FIND_YOUR_BILL_FORM_SUBMITTED', {
            policyNumber: this.policyNumber,
            zip: this.zip
          })
          .then(() => {
            this.isTransmittingData = false;
            this.goToPage('payment-info');
          })
          .catch(() => {
            this.isTransmittingData = false;

            if (this.$store.state.error === 'Invalid-Policy-Zip') {
              this.validateCredentials = true;
            } else if (this.$store.state.error === 'generalError') {
              this.goToPage('error');
            }
          });
        }
      }
    },
    // Update the validateCredentials value to enable Find My Bill button
    updateValidateCredentials() {
      if (this.validateCredentials) {
        this.validateCredentials = false;
      }
    },
    /**
     * Check whether the policy is a rejected or accepted format.
     * @returns true if the policy is an accepted format
     */
    isPolicySupported(policyNumber) {
      if (RejectedPoliciesService.isSpecialityLinesPolicy(policyNumber)) {
        return 'specialtyLinesPolicy';
      }
      switch (this.insuranceTypeKey) {
        case 'sfm':
          if (RejectedPoliciesService.isFloridaPolicy(policyNumber)) {
            return 'floridaPolicy';
          }
          break;
        case 'sfi':
          if (RejectedPoliciesService.isMissouriPolicy(policyNumber)) {
            return 'missouriPolicy';
          }
          break;
      }
      return 'goToPaymentInfo';
    }
  },
  validations: {
    policyNumber: {
      required,
      pattern: pattern(/^[a-zA-Z0-9]{9,11}$/)
    },
    zip: {
      required,
      pattern: pattern(/^\d{5}(-\d{4})?$/)
    }
  }
}
</script>

<style lang="scss" type="scss" scoped>
@import '~@/styles/variables.scss';

// Find Your Bill page has a different width than default Express Pay form width
$find-your-bill-width: 770px;

.find-your-bill {

  .content-area {
    @media (min-width: $find-your-bill-width) {
      // Ensure the global max-width of 680px is overridden to allow 770px width on Find Your Bill page
      max-width: $find-your-bill-width;
    }

    color: $black;
    position: relative;

    .description {
      font-size: 18px;
    }

    .description-mobile {
      margin-bottom: 24px;
    }

    .note {
      font-size: 14px;
      font-weight: $global-weight-normal;
    }

    .custom-margin {
      @include breakpoint(medium) {
        padding-right: 10px;
      }
    }

    .mobile-note {
      padding-top: 20px;
    }

    .help {
      font-size: 16px;
      font-weight: 400;
    }

    .input-policy-number {
      margin-bottom: 10px;
    }

    .input-zip-code {
      margin-top: 20px;
    }

    .policy-help-container {
      margin-bottom: 24px;
    }

    .bold-title {
      font-weight: $global-weight-semibold;
      font-size: 18px;
      margin-bottom: .4375rem;
    }

    .find-your-bill-next-button {
      margin-top: 30px;
      margin-left: auto;
      margin-right: auto;
    }

    .validate-credentials {
      margin-top: 24px;
      .credentials-text-error {
        color: $alert-color;
        font-weight: $global-weight-semibold;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/FindYourBill.vue